import classnames from 'classnames';
import debounce from 'lodash/debounce';
import * as React from 'react';
import { useFetchAll } from '../../common/customHooks/useFetch';
import { env } from '../../config';
import { BasicItem, ListResponse } from '../../models/search';
import { getProductsQuery } from '../../utils/queryUtils';
import { getSearchHistory } from '../../utils/searchHistory';
import { ProductListResponse } from '../ProductListWrapper/models';
import SearchBar, { KEY_STROKE_DELAY } from '../SearchBar/SearchBar';
import { SearchBarWrapperProps } from './models';
import styles from './styles.scss';
import {
  ENTITY,
  ENTITY_TEXT,
  FITMENT_SELECTOR_STORAGE_KEY,
} from '../../utils/constants';
import {
  getExistingFitment,
  updateFitmentQueryParams,
} from '../../utils/fitmentUtils';
import { isEqual } from 'lodash';

let controller = new AbortController();
let signal = controller.signal;
export const DEFAULT_MAX_RESULTS = 5;
export const DEFAULT_SECTION = [
  { maxResults: DEFAULT_MAX_RESULTS, title: 'Categories', type: 'category' },
  { maxResults: DEFAULT_MAX_RESULTS, title: 'Brands', type: 'brand' },
  { maxResults: DEFAULT_MAX_RESULTS, title: 'Products', type: 'product' },
] as SearchBarWrapperProps['sections'];

const URL_PER_SECTION = {
  brand: `${env.API_URL}/brands`,
  category: `${env.API_URL}/categories`,
  product: `${env.API_URL}/products`,
};

const MIN_SEARCH_CHARACTERS = 1;

const SearchBarWrapper = (
  {
    className,
    linkEl = 'a',
    noResultsText,
    placeholder = 'Enter keyword',
    searchButtonText = 'Search',
    showCloseIcon,
    styled,
    onSubmit,
    showNoResultsMsg = true,
    searchValue: searchString = '',
    sections = DEFAULT_SECTION,
    renderList,
    onItemClick,
    fitment,
    onClearFitment,
    defaultQueryParams = {},
  }: SearchBarWrapperProps,
  ref
) => {
  const validSections =
    !Array.isArray(sections) || sections.length === 0
      ? DEFAULT_SECTION
      : sections;

  const hasHistorySection = validSections.filter(
    (item) => item.type === 'history'
  ).length;
  const [loadSearchData, searchState, dispatch] =
    useFetchAll<
      [ListResponse<BasicItem>, ListResponse<BasicItem>, ProductListResponse]
    >();

  const loading = searchState.status === 'LOADING';
  const hasData = searchState?.data?.reduce(
    (acc, item) => acc + (item.list?.length || 0),
    0
  );

  // resultsFor is the last successful text searched
  const [resultsFor, setResultsFor] = React.useState('');
  const [searchValue, setSearchValue] = React.useState(searchString);
  // Need an internal fitmentValue to clear the fitment in the searchbar when user
  // clicks the close button on the fitment label and only trigger a search without fitment
  // when user submits the search.
  const [internalFitment, setInternalFitment] = React.useState(fitment);
  const [entity, setEntity] = React.useState(
    localStorage.getItem(ENTITY_TEXT) || ''
  );
  const [, reRender] = React.useState({});

  React.useEffect(() => {
    reset();
  }, [sections]);

  React.useEffect(() => {
    setInternalFitment(fitment);
  }, [fitment]);

  React.useEffect(() => {
    setSearchValue(searchString);
  }, [searchString]);

  React.useEffect(() => {
    window.addEventListener('PL_FITMENT_CHANGED', updateFitment);
    window.addEventListener('ENTITY_UPDATED', updateEntity);
    return () => {
      window.removeEventListener('PL_FITMENT_CHANGED', updateFitment);
      window.addEventListener('ENTITY_UPDATED', updateEntity);
    };
  }, []);

  React.useImperativeHandle(ref, () => ({
    loadData(value: string, lastFitmentValue: string) {
      loadData(value, lastFitmentValue);
    },
  }));

  React.useEffect(() => {
    // set initial fitment if it's in the storage
    const initialFitment = getExistingFitment();
    if (initialFitment) {
      setInternalFitment(initialFitment);
    }
  }, []);

  React.useEffect(() => {}, [internalFitment]);

  const updateFitment = React.useCallback((e: CustomEvent) => {
    const fitmentKeys = Object.keys(e.detail.fitment);
    const hasFitment = fitmentKeys.length > 0;
    const fitmentChanged = !isEqual(e.detail.fitment, fitment);

    if (hasFitment && fitmentKeys.length > 2) {
      setInternalFitment(Object.values(e.detail.fitment).join('|'));
    } else if (fitmentChanged && fitmentKeys.length === 0) {
      setInternalFitment('');
      localStorage.setItem(FITMENT_SELECTOR_STORAGE_KEY, '[]');
    }
  }, []);

  const updateEntity = React.useCallback((e: CustomEvent) => {
    setEntity(e.detail.entityText);
  }, []);

  const loadData = React.useCallback(
    debounce(async (value: string, lastFitmentValue: string) => {
      if (value.length >= MIN_SEARCH_CHARACTERS) {
        const requests = validSections
          .filter((item) => item.type !== 'history')
          .map((section) => ({
            query: {
              ...getProductsQuery(
                1,
                section.maxResults || 5,
                value,
                null,
                lastFitmentValue
              ),
              ...defaultQueryParams,
            },
            signal,
            url: URL_PER_SECTION[section.type],
          }));

        await loadSearchData(requests);

        setResultsFor(value);
      } else {
        reset();
      }
    }, KEY_STROKE_DELAY),
    [sections, MIN_SEARCH_CHARACTERS]
  );

  const onChange = (value: string) => {
    setSearchValue(value);
    // To prevent rendering data for old BE calls we cancel pending requests before making a new one
    if (searchState.status === 'LOADING') {
      controller.abort();
      controller = new AbortController();
      signal = controller.signal;
    }
    loadData(value, internalFitment);
  };

  const showNoResults = () =>
    !loading &&
    searchValue === resultsFor &&
    searchValue.length >= MIN_SEARCH_CHARACTERS &&
    !hasData;

  const renderNoResultsText = (message: string) => (
    <p
      className={classnames(
        {
          [styles.styledNoResults]: styled,
        },
        'Sui-SearchBar--no-results'
      )}
    >
      {message}
    </p>
  );

  const renderErrorOrNoResultsMessage = () => {
    if (searchState.status === 'REJECTED' && searchValue === resultsFor) {
      // We want to show this text if there is an error and all requests have finished.
      // We know requests have finished when searchValue === resultsFor
      return renderNoResultsText(`${searchState.error}.`);
    } else if (showNoResults()) {
      return renderNoResultsText(
        noResultsText || `No results for: ${resultsFor}`
      );
    }
    return null;
  };

  const clear = () => {
    setSearchValue('');
    reset();
  };

  const reset = () => {
    dispatch({ type: 'RESET' });
    setResultsFor('');
  };

  const clearFitment = () => {
    setInternalFitment('');
    onClearFitment?.();
    localStorage.setItem(FITMENT_SELECTOR_STORAGE_KEY, '[]');
    loadData(searchValue, '');
    const fitmentEvent = new CustomEvent('PL_FITMENT_CHANGED', {
      detail: { fitment: {} },
    });
    updateFitmentQueryParams({});
    window.dispatchEvent(fitmentEvent);
  };

  const clearFilter = () => {
    localStorage.removeItem(ENTITY);
    localStorage.removeItem(ENTITY_TEXT);
    setEntity('');
    window.dispatchEvent(
      new CustomEvent('ENTITY_UPDATED', {
        detail: {
          entityText: '',
        },
      })
    );
  };

  return (
    <>
      <SearchBar
        className={className}
        linkEl={linkEl}
        noResultsText={noResultsText}
        placeholder={placeholder}
        searchButtonText={searchButtonText}
        showCloseIcon={showCloseIcon}
        styled={styled}
        onSubmit={onSubmit}
        showNoResultsMsg={showNoResultsMsg}
        searchValue={searchValue}
        renderNoResults={renderErrorOrNoResultsMessage}
        onClear={clear}
        onChange={onChange}
        isLoading={loading}
        onClearHistory={() => {
          reRender({});
        }}
        sections={validSections.map((section, index) => {
          if (section.type === 'history') {
            const history = getSearchHistory();
            return {
              ...section,
              ...(history.length
                ? {
                    data: {
                      list: history.map((item, index) => ({
                        id: index,
                        name: item,
                      })),
                      total: history.length,
                    },
                  }
                : {}),
            };
          }

          // When we have history as visible section we need to calculate,
          // the search data loaded from BE in this way because the first items in searchState
          // array searchState.data[] are products, categories and brands (depends on the visible sections)
          const indexRemoteData = index - (hasHistorySection ? 1 : 0);
          return {
            ...section,
            ...(searchState.data?.[indexRemoteData]?.total !== undefined
              ? {
                  data: {
                    list: searchState.data?.[indexRemoteData]?.list || [],
                    total: searchState.data?.[indexRemoteData]?.total || 0,
                  },
                }
              : {}),
          };
        })}
        renderList={renderList}
        onItemClick={onItemClick}
        fitment={internalFitment}
        entity={entity}
        onClearFitment={clearFitment}
        onClearFilter={clearFilter}
      />
    </>
  );
};

export default React.forwardRef(SearchBarWrapper);
